import { Button, Card, Select, Table, Tag, TimePicker, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import dayjs from "dayjs"
import React, { useEffect, useState } from "react"

import { TimezoneSelect } from "../../../../components/Shared/TimezoneSelect.tsx"
import * as services from "../../api/services.ts"
import type { BranchConfiguration } from "../../types/types.ts"

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"]

const { Text } = Typography

interface Row {
  key: string
  label: string
  effective: React.ReactNode
  source: "customized" | "company_default"
  // Set only for a setting with no branch-override mechanism at all yet
  // (Required Information, Templates) — an honest disclosure row rather
  // than a Customize/Edit action that would do nothing real.
  notCustomizableYet?: boolean
}

export const BranchConfigurationTab = ({
  branchId,
  onSaved
}: {
  branchId: string
  onSaved: () => void
}) => {
  const [config, setConfig] = useState<BranchConfiguration | null>(null)
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [draftTimezone, setDraftTimezone] = useState<string | undefined>()
  const [draftDays, setDraftDays] = useState<string[]>([])
  const [draftHours, setDraftHours] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    null
  )

  const load = async () => {
    const response = await services.getBranchConfiguration(branchId)
    if (response.ok) setConfig(response.data)
  }

  useEffect(() => {
    load()
  }, [branchId])

  if (!config) return null

  const startEdit = (key: string) => {
    setEditingKey(key)
    if (key === "timezone") setDraftTimezone(config.timezone.value)
    if (key === "working_days") setDraftDays(config.working_days.value || [])
    if (key === "business_hours") {
      const hours = config.business_hours.value || {}
      setDraftHours([
        dayjs(hours.start || "08:00", "HH:mm"),
        dayjs(hours.end || "17:00", "HH:mm")
      ])
    }
  }

  const save = async (key: string) => {
    const patch: Record<string, unknown> = {}
    if (key === "timezone") patch.timezone = draftTimezone
    if (key === "working_days") patch.working_days = draftDays
    if (key === "business_hours") {
      patch.business_hours = {
        start: draftHours?.[0]?.format("HH:mm"),
        end: draftHours?.[1]?.format("HH:mm")
      }
    }
    await services.updateBranchConfiguration(branchId, patch)
    setEditingKey(null)
    load()
    onSaved()
  }

  const reset = async (key: string) => {
    await services.updateBranchConfiguration(branchId, { [key]: null })
    load()
    onSaved()
  }

  const rows: Row[] = [
    {
      key: "timezone",
      label: "Time zone",
      source: config.timezone.source,
      effective:
        editingKey === "timezone" ? (
          <TimezoneSelect
            className="w-64"
            value={draftTimezone}
            onChange={setDraftTimezone}
          />
        ) : (
          config.timezone.value
        )
    },
    {
      key: "working_days",
      label: "Working days",
      source: config.working_days.source,
      effective:
        editingKey === "working_days" ? (
          <Select
            mode="multiple"
            className="w-64"
            value={draftDays}
            onChange={setDraftDays}
            options={DAYS.map((d) => ({ value: d, label: d }))}
          />
        ) : (
          (config.working_days.value || []).join(", ") || "—"
        )
    },
    {
      key: "business_hours",
      label: "Business hours",
      source: config.business_hours.source,
      effective:
        editingKey === "business_hours" ? (
          <TimePicker.RangePicker
            value={draftHours}
            format="HH:mm"
            onChange={(v) => setDraftHours(v as [dayjs.Dayjs, dayjs.Dayjs])}
          />
        ) : config.business_hours.value?.start ? (
          `${config.business_hours.value.start} – ${config.business_hours.value.end}`
        ) : (
          "—"
        )
    },
    {
      key: "required_information",
      label: "Required Information",
      source: "company_default",
      notCustomizableYet: true,
      effective: "Company-wide"
    },
    {
      key: "templates",
      label: "Templates",
      source: "company_default",
      notCustomizableYet: true,
      effective: "Company-wide"
    }
  ]

  const columns: ColumnsType<Row> = [
    { title: "Setting", dataIndex: "label" },
    { title: "Effective value", dataIndex: "effective" },
    {
      title: "Source",
      dataIndex: "source",
      render: (source: string) => (
        <Tag color={source === "customized" ? "orange" : "default"}>
          {source === "customized" ? "Customized for this branch" : "Using Company HQ defaults"}
        </Tag>
      )
    },
    {
      title: "Action",
      render: (_, record) =>
        record.notCustomizableYet ? (
          <Text type="secondary" className="text-xs">
            Not yet customizable per branch
          </Text>
        ) : editingKey === record.key ? (
          <Button type="link" onClick={() => save(record.key)}>
            Save
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button type="link" onClick={() => startEdit(record.key)}>
              {record.source === "customized" ? "Edit" : "Customize"}
            </Button>
            {record.source === "customized" && (
              <Button type="link" onClick={() => reset(record.key)}>
                Reset to Company HQ defaults
              </Button>
            )}
          </div>
        )
    }
  ]

  return (
    <Card title="Effective branch configuration">
      <Table
        rowKey="key"
        columns={columns}
        dataSource={rows}
        pagination={false}
      />
    </Card>
  )
}
